import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { PageCTA } from '../components/PageCTA';
import { BarChart3, Users, Zap, Smartphone, Eye, Star, ShieldCheck, MessageSquare, TrendingUp, ArrowUpRight } from 'lucide-react';
export function ResultsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const metrics = [{
    icon: <BarChart3 size={24} data-id="element-645" />,
    stat: '+70%',
    title: 'Incremento en Autoridad',
    description: 'Arquitectura visual premium que justifica un pricing superior.'
  }, {
    icon: <Users size={24} data-id="element-646" />,
    title: 'Multiplicador de Adquisición',
    stat: '+3x',
    description: 'Eliminamos la fricción cognitiva para maximizar la conversión.'
  }, {
    icon: <Zap size={24} data-id="element-647" />,
    title: 'Eficiencia de Conversión',
    stat: '100%',
    description: 'Micro-interacciones calibradas para dirigir el comportamiento.'
  }, {
    icon: <Smartphone size={24} data-id="element-648" />,
    title: 'Omnicanalidad Nativa',
    stat: '100%',
    description: 'Rendimiento impecable en todo el espectro de dispositivos.'
  }, {
    icon: <TrendingUp size={24} data-id="element-649" />,
    title: 'Retorno de Inversión',
    stat: '+250%',
    description: 'Reducción drástica del coste de adquisición de clientes.'
  }, {
    icon: <Star size={24} data-id="element-650" />,
    title: 'Retención de Clientes',
    stat: '+85%',
    description: 'Experiencias que transforman compradores en embajadores.'
  }];
  const benefits = [{
    icon: <Eye size={20} data-id="element-651" />,
    title: 'Dominio SEO',
    description: 'Indexación prioritaria para capturar la demanda activa.',
    metric: '+85% Visibilidad'
  }, {
    icon: <Star size={20} data-id="element-652" />,
    title: 'Impacto Cognitivo',
    description: 'Interfaces que capturan la atención en menos de 3 segundos.',
    metric: '-40% Rebote'
  }, {
    icon: <ShieldCheck size={20} data-id="element-653" />,
    title: 'Confianza Institucional',
    description: 'Infraestructura robusta que elimina el escepticismo.',
    metric: '100% Legitimidad'
  }, {
    icon: <MessageSquare size={20} data-id="element-654" />,
    title: 'Canalización Ágil',
    description: 'Sistemas de contacto integrados que aceleran ventas.',
    metric: '3x Velocidad'
  }];
  const cases = [{
    type: 'Sector Gastronómico Premium',
    problem: 'Menús en PDF, reservas manuales e imagen digital que no reflejaba la calidad del local.',
    solution: 'App web para catálogos dinámicos, motor de reservas automatizado y sincronización de redes.',
    result: '+40% reservas automatizadas y elevación del posicionamiento de marca.',
    tags: ['PWA', 'Automatización', 'UI/UX']
  }, {
    type: 'Servicios B2B / B2C Locales',
    problem: 'Invisible en búsquedas locales. Web obsoleta con alta tasa de rebote y soporte innecesario.',
    solution: 'Reingeniería web con SEO local técnico, taxonomía clara y conversión directa vía WhatsApp.',
    result: 'Dominio en búsqueda local, reducción de consultas de soporte y aumento de leads.',
    tags: ['SEO Técnico', 'Arquitectura', 'APIs']
  }, {
    type: 'Consultoría de Alta Dirección',
    problem: 'Falta de autoridad digital. Onboarding manual y percepción de valor incongruente.',
    solution: 'Plataforma corporativa de autoridad con agendamiento sincronizado y hub de contenidos.',
    result: 'Automatización del embudo de captación y +30% en pricing sin impacto en conversión.',
    tags: ['Autoridad', 'Sistemas', 'Escalabilidad']
  }];
  return <div className="pt-24 bg-[#0a0a0a] min-h-screen" data-id="element-655">
      {/* Page Header */}
      <section className="py-20 border-b border-white/[0.05]" data-id="element-656">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl text-center" data-id="element-657">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-658">
            <h1 className="text-4xl md:text-5xl font-medium text-white mb-6 tracking-tight" data-id="element-659">
              Métricas que transforman tu empresa
            </h1>
            <p className="text-lg text-gray-400 leading-relaxed font-normal max-w-2xl mx-auto" data-id="element-660">
              No evaluamos nuestro rendimiento basándonos en estética
              superficial, sino en la aceleración del crecimiento y la
              optimización operativa que inyectamos en tu modelo de negocio.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Metrics Grid */}
      <section className="py-20 border-b border-white/[0.05]" data-id="element-661">
        <div className="container mx-auto px-6 md:px-12 max-w-5xl" data-id="element-662">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.05] rounded-2xl overflow-hidden border border-white/[0.05]" data-id="element-663">
            {metrics.map((metric, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} className="bg-[#111] p-8 hover:bg-[#131313] transition-colors flex flex-col" data-id="element-664">
                <div className="flex items-center gap-3 mb-6" data-id="element-665">
                  <div className="icon-premium !w-10 !h-10 !rounded-lg scale-90 origin-left" data-id="element-666">
                    {metric.icon}
                  </div>
                  <div className="text-3xl font-medium text-gradient tracking-tight" data-id="element-667">
                    {metric.stat}
                  </div>
                </div>
                <h3 className="text-base font-medium text-white mb-2" data-id="element-668">
                  {metric.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed" data-id="element-669">
                  {metric.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Benefits List */}
      <section className="py-20 border-b border-white/[0.05]" data-id="element-670">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl" data-id="element-671">
          <div className="mb-12" data-id="element-672">
            <h2 className="text-2xl font-medium text-white mb-3 tracking-tight" data-id="element-673">
              Beneficios tangibles
            </h2>
            <p className="text-gray-400 text-sm" data-id="element-674">
              Lo que experimentarás en el día a día de tu negocio.
            </p>
          </div>

          <div className="card-premium !overflow-hidden divide-y divide-white/[0.05]" data-id="element-675">
            {benefits.map((benefit, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} className="p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 hover:bg-[#131313] transition-colors" data-id="element-676">
                <div className="w-10 h-10 rounded-lg bg-white/[0.05] border border-white/[0.08] flex items-center justify-center shrink-0 text-white" data-id="element-677">
                  {benefit.icon}
                </div>
                <div className="flex-1" data-id="element-678">
                  <h3 className="text-sm font-medium text-white mb-1" data-id="element-679">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-680">{benefit.description}</p>
                </div>
                <div className="shrink-0" data-id="element-681">
                  <span className="badge-premium !text-[11px] !uppercase !tracking-wider" data-id="element-682">
                    {benefit.metric}
                  </span>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20" data-id="element-683">
        <div className="container mx-auto px-6 md:px-12 max-w-5xl" data-id="element-684">
          <div className="mb-12" data-id="element-685">
            <h2 className="text-2xl font-medium text-white mb-3 tracking-tight" data-id="element-686">
              Casos de éxito
            </h2>
            <p className="text-gray-400 text-sm" data-id="element-687">
              Ejemplos reales de cómo nuestro ecosistema resuelve problemas
              específicos.
            </p>
          </div>

          <div className="space-y-6" data-id="element-688">
            {cases.map((caseStudy, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="card-premium p-6 md:p-8" data-id="element-689">
                <div className="flex justify-between items-start mb-6" data-id="element-690">
                  <h3 className="text-lg font-medium text-white" data-id="element-691">
                    {caseStudy.type}
                  </h3>
                  <ArrowUpRight size={18} className="text-gray-500" data-id="element-692" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-693">
                  <div data-id="element-694">
                    <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-2 block" data-id="element-695">
                      El desafío
                    </span>
                    <p className="text-sm text-gray-400 leading-relaxed" data-id="element-696">
                      {caseStudy.problem}
                    </p>
                  </div>
                  <div data-id="element-697">
                    <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-2 block" data-id="element-698">
                      La solución
                    </span>
                    <p className="text-sm text-gray-300 leading-relaxed" data-id="element-699">
                      {caseStudy.solution}
                    </p>
                  </div>
                  <div data-id="element-700">
                    <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-2 block" data-id="element-701">
                      El resultado
                    </span>
                    <p className="text-sm text-white leading-relaxed font-medium" data-id="element-702">
                      {caseStudy.result}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-6 border-t border-white/[0.05]" data-id="element-703">
                  {caseStudy.tags.map((tag, i) => <span key={i} className="text-[11px] font-medium px-2.5 py-1 bg-white/[0.03] text-gray-400 rounded-md border border-white/[0.05] uppercase tracking-wider" data-id="element-704">
                      {tag}
                    </span>)}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      <PageCTA data-id="element-705" />
    </div>;
}